import * as log from 'loglevel';
import { resolveByCountry, autocompleteLocations } from '@codex/helpers';

const DEFAULT_SEARCH_RESTRICTIONS = ['premise', 'administrative_area_level_3', 'administrative_area_level_2'];
const RETAIL_LOCATIONS = ['currys', 'pc world', 'pcworld', 'p c world', 'carphone'];


export function searchSuggestions(searchTerm) {
  return autocompleteLocations(searchTerm, ['(regions)'], resolveByCountry(['uk', 'im'], 'irl'))
    .then(
      (predictions) => getLocations(searchTerm, predictions, DEFAULT_SEARCH_RESTRICTIONS)
    ).catch(
      (e) => {
        if (e === 'ZERO_RESULTS') {
          autocompleteLocations(searchTerm, ['geocode'], resolveByCountry(['uk', 'im'], 'irl'))
            .then(
              (predictions) => getLocations(searchTerm, predictions, DEFAULT_SEARCH_RESTRICTIONS)
            ).catch(
              () => {
                console.warn(`NOT FOUND LOCATIONS FOR SEARCH TERM: ${searchTerm}`);
                return [];
              }
            );
        }
        console.warn(`NOT FOUND LOCATIONS FOR SEARCH TERM: ${searchTerm}`);
        return [];
      }
    );
}

export function getGeolocation() {
  return new Promise((resolve) => {
    if (!(navigator && navigator.geolocation)) {
      resolve({ ok: false, errorMessage: 'Geolocation not supported in your browser.' });
    }

    function onsuccess(position) {

      const location = {
        coords: [position.coords.latitude, position.coords.longitude]
      };
      resolve({ ok: true, location: location });
    }

    function onerror(positionError) {
      log.error('Gathering position from browser failed:');
      log.error(positionError);

      let errorMessage;
      switch (positionError.code) {
        case 1: {
          errorMessage = "The acquisition of the geolocation from browser failed because your browser don't have " +
            'the permission to do it. Permit location services in your browser.';
          break;
        }
        case 2: {
          errorMessage = 'The acquisition of the geolocation from browser failed because your browser returned ' +
            'an internal error when trying to gather the geolocation.';
          break;
        }
        case 3: {
          errorMessage = 'The time (10 seconds) allowed to acquire the geolocation was reached before the information ' +
            'was obtained.';
          break;
        }
        default: {
          errorMessage = 'The acquisition of the geolocation from browser failed because of ' +
            'unknown error in your browser.';
        }
      }

      resolve({ ok: false, errorMessage: errorMessage });
    }
    const options = {
      enableHighAccuracy: true,
      timeout: 10000
    };
    navigator.geolocation.getCurrentPosition(onsuccess, onerror, options);
  });
}

// @description Resolve location descriptor from Google API to the Postcode & LatLong */
export function resolveLocationByGoogleAPI(descriptor) {

  return new Promise((resolve, reject) => {
    if (descriptor.postcode) {
      // We (wrongly) assume that the postcode is always valid and resolve() immediately
      // We probably want to expand a postcode only descriptor to get a full validated location & description...

      // Sometimes id and description are missing in descriptor so it has to be added to it
      if (!descriptor.id) {
        descriptor.id = descriptor.postcode;
        descriptor.description = resolveByCountry('UK', 'IE');
      }

      resolve(descriptor);
      return;
    } if (descriptor.coords.length > 0) {
      getPostCode()
        .then(
          () => {
            // Sometimes id and description are missing in descriptor so it has to be added to it
            if (!descriptor.id) {
              descriptor.id = descriptor.postcode;
              descriptor.description = resolveByCountry('UK', 'IE');
            }

            resolve(descriptor);
          },
          (reason) => {
            reject(reason);
          });
      return;
    }

    function resolveLatLong(results, status) {
      if (status === window.google.maps.GeocoderStatus.OK) {
        const geometry = results[0].geometry;
        descriptor.coords.push(parseFloat(geometry.location.lat()));
        descriptor.coords.push(parseFloat(geometry.location.lng()));
        resolve(getPostCode());
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject(`Error Geocoding PlaceId: ${descriptor.id}`, status);
      }
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: descriptor.id }, resolveLatLong);
  });


  function getPostCode() {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      /** @description Google Geocode callback for trying to get postcode from geocode search results
       @param {Array} results
       @param {google.maps.GeocoderStatus} status
       @returns {void} */
      function parseGeocodeResultsForGatheringPostcode(results, status) {
        if (status === window.google.maps.GeocoderStatus.OK) {

          // Try to find a specific postcode for this location from postal code result ("postal_code")
          // or from nearby address result ("street_address")
          // or from nearby route result ("route")
          tryToGatherPostcodeFromGeocodeResponse(results, 'postal_code') || // eslint-disable-line no-unused-expressions
          tryToGatherPostcodeFromGeocodeResponse(results, 'street_address') ||
          tryToGatherPostcodeFromGeocodeResponse(results, 'route');

          if (descriptor.postcode || resolveByCountry(false, true)) {
            // if the postcode is found this Promise is resolved
            // if the current page is Ireland page, this Promise is also resolved
            // because Ireland postcode (eircode) is not required by
            // PHP server web servicesfor Ireland so it's not necessary to send it
            resolve(descriptor);
          } else {
            // This is fallback for UK Currys/PC World where postcode is required for PHP web services
            // The postcode doesn't have to be found by previous code in some rare cases (3% of all UK locations)
            // If it happens, we try to gather postcode by finding the nearest Post Office for this location
            // beacause every UK Post Office should have postcode
            getPostCodeOfTheNearestPostOffice();
          }
        } else {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(`Error Geocoding Location: ${status}`);
        }
      }

      /** @description parse the array of Google Geocode addresses in order
       * to try gather UK postcode. if it finds postcode it saves the postcode to descriptor.postcode
       @param {Array} results
       @param {string} type Address Type
       @returns {boolean} true - postcode was found
       false - postcode wasn't found
       */
      function tryToGatherPostcodeFromGeocodeResponse(results, type) {
        for (let i = 0; i < results.length; i++) {
          if (results[i].types.indexOf(type) !== -1) {
            const components = results[i].address_components;
            for (let j = 0; j < components.length; j++) {
              if (components[j].types[0] === 'postal_code' && // We only want a type that is exactly a postcode
                components[j].types.length === 1) { // in order to eliminate postcode prefixes in case of "types" : [ "postal_code", "postal_code_prefix" ]
                // eslint-disable-next-line no-param-reassign
                descriptor.postcode = components[j].long_name;
                break;
              }
            }
            if (descriptor.postcode) {
              if (!descriptor.location) { // Assign postcode as location string if none exists
                // eslint-disable-next-line no-param-reassign
                descriptor.location = descriptor.postcode;
              }
              return true;
            }
            return false;
          }
        }
        return false;
      }

      /**
       * Try to find nearest post office for this location using Google Maps API and gather postcode from it
       * beacause every UK Post Office should have postcode.
       */
      function getPostCodeOfTheNearestPostOffice() {
        const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

        const placesServiceRequest = {
          location: new window.google.maps.LatLng(descriptor.coords[0], descriptor.coords[1]),
          radius: 2000,
          type: ['post_office']
        };

        // following method searches for the nearest post office of the current location
        // in case of error current promise is rejected
        placesService.nearbySearch(placesServiceRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const geoPostOfficeRequest = {
              placeId: results[0].place_id
            };

            // if post office is found we gather postcode from this post office by reverse geocode call
            // if postcode is found, it's added to descriptor.postcode and Promise is resloved
            // if not found Promise is rejected
            geocoder.geocode(geoPostOfficeRequest, (postOfficeResults, currentStatus) => {
              if (currentStatus === window.google.maps.GeocoderStatus.OK) {
                for (let i = 0; i < postOfficeResults[0].address_components.length; i++) {
                  const currentAddressComponent = postOfficeResults[0].address_components[i];

                  if (currentAddressComponent.types[0] === 'postal_code' && // We only want a type that is exactly a postcode
                    currentAddressComponent.types.length === 1) { // in order to eliminate postcode prefixes in case of "types" : [ "postal_code", "postal_code_prefix" ]
                    // eslint-disable-next-line no-param-reassign
                    descriptor.postcode = currentAddressComponent.long_name;
                    resolve(descriptor);
                    return;
                  }
                }
                // eslint-disable-next-line prefer-promise-reject-errors
                reject(`Error finding postcode using Geolocation: ${currentStatus}`);
              } else {
                // eslint-disable-next-line prefer-promise-reject-errors
                reject(`Error finding postcode using Geolocation: ${currentStatus}`);
              }

            });
          } else {
            // eslint-disable-next-line prefer-promise-reject-errors
            reject(`Error finding post office for gathering postcode: ${status}`);
          }
        });
      }

      const geoRequest = {
        location: {
          lat: descriptor.coords[0],
          lng: descriptor.coords[1]
        }
      };

      // Finds geocode addresses for gathering postcode. Google JS API reverse geocoding is used
      geocoder.geocode(geoRequest, parseGeocodeResultsForGatheringPostcode);
    });
  }
}

/** @param {string} term
 @param {Array<Object>} predictions
 @param {Array<string>} restrictions
 @returns {Array<LocationDescriptors>}
 @description Return an array of valid LocationsDescriptors from Google Places predictions */
function getLocations(term, predictions, restrictions) {
  const descriptors = [];
  for (let i = 0; i < predictions.length; i++) {
    const descriptor = createLocationDescriptor(term, predictions[i]);

    let restricted = false;
    if (restrictions.length > 0 && !descriptor.retailLocation) {
      for (let t = 0; t < descriptor.types.length; t++) {
        if (restrictions.indexOf(descriptor.types[t]) !== -1) {
          restricted = true;
          break;
        }
      }
    }
    if (!restricted) {
      descriptors.push(descriptor);
    }
  }
  return descriptors;
}

// @param {string} term
// @param {object} prediction Google Places prediction object */
function createLocationDescriptor(searchTerm, prediction) {
  const locationDescriptor = {};

  locationDescriptor.coords = [];
  locationDescriptor.term = searchTerm;
  locationDescriptor.id = prediction.place_id;
  locationDescriptor.types = prediction.types;
  locationDescriptor.useCase = { page: '', description: '' };

  const descriptions = []; // Any term after the actual matched search string

  // eslint-disable-next-line no-param-reassign
  searchTerm = searchTerm.toLowerCase();
  for (let i = 0; i < prediction.terms.length; i++) {
    const term = prediction.terms[i].value.toLowerCase();
    // Find the prediction that exactly matches the search term or use as a description
    if (!locationDescriptor.location && term >= parseInt(searchTerm, 10)) { // Intentional typecast
      // If the searchTerm begins with a number, we assume we've matched a street address number - try to expand it.
      // Eg. "45" >= parseInt("450 Fulham Road")
      locationDescriptor.location = `${prediction.terms[i].value} ${prediction.terms[i + 1].value}` || '';
      locationDescriptor.types = ['street_address'];
    } else if (!locationDescriptor.location && term.indexOf(searchTerm) === 0) {
      // Eg. String("45  oxford street").indexOf("45 oxf") === 0
      locationDescriptor.location = prediction.terms[i].value;
      // If the matched search term begins with a number, again we assume a numbered street address
      if (!Number.isNaN(parseInt(searchTerm, 10))) {
        locationDescriptor.types = ['street_address'];
      }
    } else {
      descriptions.push(prediction.terms[i].value);
    }
  }

  if (typeof locationDescriptor.location === 'undefined') {
    // If no term exactly matches we assume a non-exact match against the first term
    locationDescriptor.location = prediction.terms[0].value;
    descriptions.shift(); // Drop first description as we use the first one for location
    locationDescriptor.exact = false;
  }

  locationDescriptor.description = descriptions.join(', ');

  // Is this a store location?
  if (prediction.types.indexOf('premise') !== -1) {
    const location = locationDescriptor.location.toLowerCase();
    for (let i = 0; i < RETAIL_LOCATIONS.length; i++) {
      if (location.indexOf(RETAIL_LOCATIONS[i]) !== -1) {
        locationDescriptor.retailLocation = true;
        break;
      }
    }
  }

  return locationDescriptor;
}
